import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, GiftIcon, ShieldCheckIcon } from 'lucide-react';
import { FadeIn } from './FadeIn';
import { trackAddToCart } from '../lib/pixel';
export function Bonuses() {
  const bonuses = [{
    image: 'https://cdn.magicpatterns.com/uploads/fTbWv17Cj3aGK3idYQJWRH/Bono1.png',
    badge: 'BONO #1 — GRATIS',
    title: 'KIT ANTI-IMPULSO DE 5 MINUTOS',
    subtitle: 'El protocolo de respuesta inmediata para cortar el impulso antes de que te arrastre.',
    story: ['Hay un momento exacto donde casi todo el mundo pierde.', 'No es cuando planeas fallar. Es cuando aparece el impulso de repente, estás solo, estás cansado y tu cerebro entra en piloto automático.', 'En ese momento no necesitas motivación. No necesitas fuerza de voluntad. Necesitas un sistema de respuesta exacto, simple y ejecutable en segundos.', 'Eso es exactamente lo que hace este kit.'],
    includes: ['El protocolo de 5 pasos para neutralizar el impulso en tiempo real', 'La checklist de emergencia para no improvisar bajo presión', 'Las frases exactas para romper el diálogo interno que te sabotea', 'Los 7 detonantes más comunes y cómo anticiparte a cada uno', 'Un ejemplo aplicado paso a paso para la situación más crítica: la noche', 'Una tarjeta resumen imprimible lista para tener en tu mesa de noche', 'Versión móvil de una página para usarla en el momento exacto que la necesites'],
    who: 'Para cualquier persona que haya intentado resistir el impulso "pensando mejor" y haya perdido de todas formas. Este kit no pide que pienses más. Pide que ejecutes más rápido.',
    why: 'El impulso tiene una ventana de vida corta. Si lo interrumpes en los primeros 5 minutos, la intensidad baja sola. Este protocolo fue diseñado para darte una acción concreta en cada uno de esos minutos críticos.',
    quote: 'No necesitas ganar perfecto. Necesitas interrumpir el patrón una vez más.'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/g6Bu2LtN4P6tBdM4yUKtJU/Bono2.png',
    badge: 'BONO #2 — GRATIS',
    title: 'MAPA DE DETONANTES OCULTOS',
    subtitle: 'El workbook de autodiagnóstico para descubrir exactamente qué dispara tu patrón antes de que el impulso te gane.',
    story: ['¿Alguna vez has sentido que recaes "sin razón"?', 'Como si de repente aparecieras en el momento más bajo sin saber cómo llegaste ahí.', 'La verdad es que la recaída casi nunca empieza en el momento exacto en que caes. Empieza antes. Con señales pequeñas que no aprendiste a leer.', 'Una emoción no procesada. Un horario específico. Un pensamiento automático que siempre dice lo mismo. Un contexto repetido donde tu cerebro ya tiene instrucciones guardadas.', 'Cuando ves el patrón completo, dejas de pelear a ciegas.'],
    includes: ['Los 4 tipos de detonantes que más activan el impulso (físicos, emocionales, ambientales y mentales)', 'El test de autodiagnóstico para identificar tus patrones personales exactos', 'El framework de la cadena de recaída para ver cómo se construye antes de que explote', 'El tracker de 7 días para convertir episodios en inteligencia útil', 'La guía para usar tu claridad como prevención real', 'El mapa final de riesgo personal con tu resumen estratégico', 'Plantilla imprimible para tener tu diagnóstico visible'],
    who: 'Para cualquier persona que sienta que siempre recae en el mismo punto sin entender por qué. Este workbook convierte la culpa en claridad y la confusión en estrategia.',
    why: 'No puedes cambiar lo que no ves. Este workbook te obliga a mirarte sin juicio y con herramientas. Una vez identificas el detonante real, puedes intervenir antes del pico en lugar de intentar recuperarte después de caer.',
    quote: 'No estás roto. Solo no habías visto el patrón completo. Ahora sí.'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/2fwU3KP6rGM9KmmNuGPb3E/Bono3.png',
    badge: 'BONO #3 — GRATIS',
    title: 'RITUAL DE CIERRE NOCTURNO',
    subtitle: 'El protocolo de 10 minutos para convertir tu hora más peligrosa en una rutina de control, calma y cierre limpio.',
    story: ['La mayoría no pierde el control durante el día. Lo pierde de noche.', 'Cuando el cansancio baja la guardia. Cuando la soledad amplifica el impulso. Cuando ya no queda energía para resistir. Cuando el celular está a un brazo de distancia y la oscuridad hace el resto.', 'Ese momento tiene nombre. Se llama tu ventana de mayor vulnerabilidad.', 'Y si no tienes una rutina de cierre diseñada para ese momento, lo más probable es que tu cerebro repita el mismo patrón de siempre.', 'Este ritual fue creado para protegerte exactamente ahí.'],
    includes: ['Las 5 fases del ritual de cierre nocturno con instrucciones exactas por fase', 'La secuencia de 10 minutos para bajar activación y entrar limpio al sueño', 'Los 5 errores nocturnos que reactivan el patrón sin que lo notes', 'La plantilla personalizable para adaptar el ritual a tu horario y contexto', 'La checklist nocturna de 8 puntos para verificar tu cierre cada noche', 'Un ejemplo aplicado completo para la situación más común: cansancio + soledad + impulso leve', 'La tarjeta resumen imprimible para tener en tu mesa de noche'],
    who: 'Para cualquier persona que sepa que su momento más peligroso es la noche y que hasta ahora no tenía ninguna estructura para protegerse en esa franja. Este ritual no requiere energía extra ni motivación. Solo requiere seguir una secuencia.',
    why: 'La noche no te gana por fuerza. Te gana por ausencia de ritual. Cuando tienes una secuencia clara de cierre, tu cerebro deja de buscar estimulación por hábito y empieza a asociar ese momento con calma y control.',
    quote: 'No todas las victorias son visibles. Algunas empiezan en silencio, antes de dormir.'
  }];
  return <section className="py-24 px-6 bg-[#09090b] relative" data-id="element-14">
      <div className="max-w-3xl mx-auto" data-id="element-15">
        {/* Intro Section */}
        <FadeIn data-id="element-16">
          <div className="text-center mb-16" data-id="element-17">
            <h2 className="font-bebas text-5xl sm:text-6xl uppercase tracking-wide text-white mb-6" data-id="element-18">
              Pero eso no es todo lo que{' '}
              <span className="text-amber-500" data-id="element-19">recibes hoy.</span>
            </h2>
            <p className="text-xl text-zinc-300 leading-relaxed mb-6" data-id="element-20">
              Cuando haces tu inversión hoy, no solo recibes el Protocolo
              Dopamina Cero.
            </p>
            <p className="text-xl text-zinc-300 leading-relaxed mb-8" data-id="element-21">
              Recibes <strong data-id="element-22">3 herramientas adicionales</strong> diseñadas
              específicamente para los momentos donde la mayoría falla: el
              impulso repentino, el patrón oculto y la noche.
            </p>
            <p className="text-xl text-amber-500 font-semibold mb-12" data-id="element-23">
              Estos 3 bonos tienen un valor real de $141 USD. Hoy los recibes
              completamente gratis.
            </p>

            <div className="flex items-center gap-4 mb-16" data-id="element-24">
              <div className="h-px bg-amber-500/30 flex-1" data-id="element-25" />
              <span className="font-bebas text-2xl uppercase tracking-widest text-zinc-500" data-id="element-26">
                LO QUE RECIBES ADEMÁS DEL PROTOCOLO PRINCIPAL:
              </span>
              <div className="h-px bg-amber-500/30 flex-1" data-id="element-27" />
            </div>
          </div>
        </FadeIn>

        {/* Bonus Cards */}
        <div className="space-y-16 mb-24" data-id="element-28">
          {bonuses.map((bonus, index) => <FadeIn key={index} delay={0.1} data-id="element-29">
              <div className={`relative bg-zinc-900/40 border border-zinc-800 rounded-2xl overflow-hidden ${index % 2 === 0 ? 'bg-[#111113]' : 'bg-[#18181b]'}`} data-id="element-30">
                {/* Top Accent Line */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-amber-500" data-id="element-31" />

                {/* Bonus Image */}
                <div className="flex justify-center bg-zinc-950/50 py-8 px-6" data-id="element-32">
                  <img src={bonus.image} alt={bonus.title} className="w-48 sm:w-56 rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.5)]" data-id="element-33" />
                </div>

                <div className="p-8 sm:p-10" data-id="element-34">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 px-4 py-1.5 rounded-full mb-6" data-id="element-35">
                    <GiftIcon className="w-4 h-4 text-amber-500" data-id="element-36" />
                    <span className="text-amber-500 font-bold text-sm tracking-widest uppercase" data-id="element-37">
                      {bonus.badge}
                    </span>
                  </div>

                  {/* Title & Value */}
                  <h3 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide text-white mb-2" data-id="element-38">
                    {bonus.title}
                  </h3>
                  <div className="flex items-center gap-3 mb-8" data-id="element-39">
                    <span className="text-zinc-500 line-through font-medium" data-id="element-40">
                      Valor real: $47 USD
                    </span>
                    <span className="text-amber-500 font-bold" data-id="element-41">
                      → Hoy: GRATIS
                    </span>
                  </div>

                  {/* Subtitle & Story */}
                  <p className="text-xl text-white font-medium leading-relaxed mb-6" data-id="element-42">
                    {bonus.subtitle}
                  </p>
                  <div className="space-y-4 mb-10" data-id="element-43">
                    {bonus.story.map((paragraph, pIndex) => <p key={pIndex} className="text-zinc-400 leading-relaxed" data-id="element-44">
                        {paragraph}
                      </p>)}
                  </div>

                  {/* What's Included */}
                  <div className="bg-zinc-950/50 rounded-xl p-6 sm:p-8 mb-10 border border-zinc-800/50" data-id="element-45">
                    <h4 className="font-bebas text-2xl uppercase tracking-wide text-white mb-6" data-id="element-46">
                      ¿Qué incluye?
                    </h4>
                    <ul className="space-y-4" data-id="element-47">
                      {bonus.includes.map((item, iIndex) => <li key={iIndex} className="flex gap-3 text-zinc-300" data-id="element-48">
                          <CheckIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-49" />
                          <span className="leading-relaxed" data-id="element-50">{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Who & Why */}
                  <div className="grid sm:grid-cols-2 gap-8 mb-10" data-id="element-51">
                    <div data-id="element-52">
                      <h4 className="text-white font-bold mb-3" data-id="element-53">
                        Para quién es este bono:
                      </h4>
                      <p className="text-zinc-400 leading-relaxed text-sm" data-id="element-54">
                        {bonus.who}
                      </p>
                    </div>
                    <div data-id="element-55">
                      <h4 className="text-white font-bold mb-3" data-id="element-56">
                        Por qué funciona:
                      </h4>
                      <p className="text-zinc-400 leading-relaxed text-sm" data-id="element-57">
                        {bonus.why}
                      </p>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="border-l-4 border-amber-500 pl-6 py-2 mb-8" data-id="element-58">
                    <p className="text-lg text-amber-500/90 italic font-medium" data-id="element-59">
                      "{bonus.quote}"
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="bg-amber-500/5 rounded-lg p-4 text-center border border-amber-500/10" data-id="element-60">
                    <p className="text-sm" data-id="element-61">
                      <span className="text-zinc-500 line-through mr-2" data-id="element-62">
                        Valor real: $47 USD
                      </span>
                      <span className="text-amber-500 font-bold uppercase tracking-wide" data-id="element-63">
                        Hoy lo recibes: GRATIS con tu compra
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>)}
        </div>

        {/* Summary Table */}
        <FadeIn data-id="element-64">
          <div className="bg-zinc-950 border border-amber-500/30 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(245,158,11,0.05)] mb-20" data-id="element-65">
            <div className="bg-amber-500/10 py-6 px-8 border-b border-amber-500/20" data-id="element-66">
              <h3 className="font-bebas text-3xl uppercase tracking-wide text-center text-amber-500" data-id="element-67">
                Todo lo que recibes hoy:
              </h3>
            </div>

            <div className="p-6 sm:p-8" data-id="element-68">
              <div className="overflow-x-auto" data-id="element-69">
                <table className="w-full text-left border-collapse min-w-[500px]" data-id="element-70">
                  <thead data-id="element-71">
                    <tr className="border-b border-zinc-800" data-id="element-72">
                      <th className="pb-4 text-zinc-500 font-medium text-sm uppercase tracking-wider" data-id="element-73">
                        Producto / Bono
                      </th>
                      <th className="pb-4 text-zinc-500 font-medium text-sm uppercase tracking-wider text-right" data-id="element-74">
                        Valor Real
                      </th>
                      <th className="pb-4 text-amber-500 font-medium text-sm uppercase tracking-wider text-right" data-id="element-75">
                        Hoy
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-300" data-id="element-76">
                    <tr className="border-b border-zinc-800/50" data-id="element-77">
                      <td className="py-5 flex items-center gap-3" data-id="element-78">
                        <CheckIcon className="w-5 h-5 text-amber-500" data-id="element-79" />
                        <span className="font-medium text-white" data-id="element-80">
                          Protocolo Dopamina Cero: El Reinicio Neuroquímico de
                          21 Días
                        </span>
                      </td>
                      <td className="py-5 text-right text-zinc-500 line-through" data-id="element-81">
                        $97 USD
                      </td>
                      <td className="py-5 text-right text-white font-bold" data-id="element-82">
                        Incluido
                      </td>
                    </tr>
                    <tr className="border-b border-zinc-800/50" data-id="element-83">
                      <td className="py-5 flex items-center gap-3" data-id="element-84">
                        <GiftIcon className="w-5 h-5 text-amber-500" data-id="element-85" />
                        <span data-id="element-86">Bono #1: Kit Anti-Impulso de 5 Minutos</span>
                      </td>
                      <td className="py-5 text-right text-zinc-500 line-through" data-id="element-87">
                        $47 USD
                      </td>
                      <td className="py-5 text-right text-amber-500 font-bold" data-id="element-88">
                        GRATIS
                      </td>
                    </tr>
                    <tr className="border-b border-zinc-800/50" data-id="element-89">
                      <td className="py-5 flex items-center gap-3" data-id="element-90">
                        <GiftIcon className="w-5 h-5 text-amber-500" data-id="element-91" />
                        <span data-id="element-92">Bono #2: Mapa de Detonantes Ocultos</span>
                      </td>
                      <td className="py-5 text-right text-zinc-500 line-through" data-id="element-93">
                        $47 USD
                      </td>
                      <td className="py-5 text-right text-amber-500 font-bold" data-id="element-94">
                        GRATIS
                      </td>
                    </tr>
                    <tr className="border-b border-zinc-800/50" data-id="element-95">
                      <td className="py-5 flex items-center gap-3" data-id="element-96">
                        <GiftIcon className="w-5 h-5 text-amber-500" data-id="element-97" />
                        <span data-id="element-98">Bono #3: Ritual de Cierre Nocturno</span>
                      </td>
                      <td className="py-5 text-right text-zinc-500 line-through" data-id="element-99">
                        $47 USD
                      </td>
                      <td className="py-5 text-right text-amber-500 font-bold" data-id="element-100">
                        GRATIS
                      </td>
                    </tr>
                  </tbody>
                  <tfoot data-id="element-101">
                    <tr data-id="element-102">
                      <td className="pt-6 pb-2 text-zinc-400 font-bold uppercase tracking-wider" data-id="element-103">
                        VALOR TOTAL
                      </td>
                      <td className="pt-6 pb-2 text-right text-zinc-500 line-through font-bold" data-id="element-104">
                        $238 USD
                      </td>
                      <td className="pt-6 pb-2" data-id="element-105"></td>
                    </tr>
                    <tr data-id="element-106">
                      <td className="py-2 text-amber-500 font-bold text-xl uppercase tracking-wider" data-id="element-107">
                        TU INVERSIÓN HOY
                      </td>
                      <td className="py-2" data-id="element-108"></td>
                      <td className="py-2 text-right text-white font-bebas text-4xl tracking-wide" data-id="element-109">
                        $6.36 USD
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Closing Copy & CTA */}
        <FadeIn data-id="element-110">
          <div className="text-center max-w-2xl mx-auto" data-id="element-111">
            <p className="text-2xl text-white font-medium mb-4" data-id="element-112">
              Todo esto por una sola inversión de{' '}
              <span className="text-amber-500 font-bold" data-id="element-113">$6.36 USD.</span>
            </p>
            <p className="text-xl text-zinc-400 mb-8" data-id="element-114">
              No <span className="line-through" data-id="element-115">$238</span>. No{' '}
              <span className="line-through" data-id="element-116">$97</span>. No{' '}
              <span className="line-through" data-id="element-117">$47</span>.
            </p>

            <div className="font-bebas text-8xl text-amber-500 leading-none mb-8 drop-shadow-[0_0_15px_rgba(245,158,11,0.2)]" data-id="element-118">
              $6.36 <span className="text-4xl text-amber-500/80" data-id="element-119">USD</span>
            </div>

            <p className="text-lg text-zinc-300 mb-8" data-id="element-120">
              Una sola vez. Sin suscripción. Sin pagos futuros. Sin condiciones.
            </p>

            <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6 mb-12 flex flex-col sm:flex-row items-center gap-4 text-left" data-id="element-121">
              <div className="bg-amber-500/10 p-3 rounded-full shrink-0" data-id="element-122">
                <ShieldCheckIcon className="w-8 h-8 text-amber-500" data-id="element-123" />
              </div>
              <div data-id="element-124">
                <h4 className="text-white font-bold mb-1" data-id="element-125">
                  Garantía completa de 7 días:
                </h4>
                <p className="text-zinc-400 text-sm" data-id="element-126">
                  Si no ves ningún cambio, te devolvemos el dinero sin
                  preguntas. El único riesgo real aquí es quedarte donde estás.
                </p>
              </div>
            </div>

            <p className="text-xl text-white font-bold mb-6" data-id="element-127">
              👇 Haz clic abajo y empieza hoy
            </p>

            <motion.a href="https://renacelibre.com/cart/42860714426445:1" onClick={() => trackAddToCart()} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="block w-full bg-amber-500 text-zinc-950 font-bold text-lg sm:text-xl py-6 px-4 rounded-xl uppercase tracking-wide shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] transition-shadow mb-4" data-id="element-128">
              Quiero El Protocolo + Los 3 Bonos Gratis
            </motion.a>

            <p className="text-sm text-zinc-500" data-id="element-129">
              Acceso inmediato. Descarga directa. Sin espera.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}